/**
 * PreferredDriverPicker — lets a vendor pin a default driver for new orders.
 * Rendered inside VendorSettingsPage after MarketplaceSetupPanel.
 */
import React, { useEffect, useState } from 'react';
import { Bike, User, Loader2, X, Star } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const PreferredDriverPicker = ({ vendorId, impersonating }) => {
  const params = impersonating && vendorId ? { vendor_id: vendorId } : undefined;
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await api.get('/vendor-admin/preferred-driver', { params }).then((res) => res.data);
      setData(r);
      setSelected(r.preferred_driver_id || null);
    } catch (e) { toast.error('Could not load drivers'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendorId]);

  const save = async (driverId) => {
    setSaving(true);
    try {
      await api.post('/vendor-admin/preferred-driver', { driver_id: driverId || null }, { params });
      setSelected(driverId || null);
      toast.success(driverId ? 'Preferred driver saved.' : 'Preferred driver cleared.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!data) return null;
  const pref = data.drivers.find((d) => d.driver_id === selected);

  return (
    <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="preferred-driver-picker">
      <div className="flex items-center gap-2 mb-3">
        <User className="h-5 w-5 text-emerald-600" />
        <h2 className="font-semibold text-slate-900">Preferred driver</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        We&apos;ll try to assign this driver first for new orders. If they&apos;re offline or busy, the nearest available driver is picked instead. Only drivers in your zone or city and Super Drivers appear here.
      </p>

      {pref && (
        <div className="mb-3 p-3 rounded-xl border border-emerald-200 bg-emerald-50/50 flex items-center gap-3" data-testid="preferred-driver-active">
          <div className="h-10 w-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold">
            {pref.name?.split(' ').map((p) => p[0]).slice(0, 2).join('') || 'D'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-slate-900 truncate">
              {pref.name}
              {pref.vehicle_plate ? <span className="ml-1 text-slate-400 font-normal">• {pref.vehicle_plate}</span>
                : pref.driver_id ? <span className="ml-1 text-slate-400 font-normal">• #{String(pref.driver_id).slice(-4)}</span> : null}
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 text-amber-400 fill-amber-400" /> {(pref.rating || 5).toFixed(1)}</span>
              <span className="text-slate-300">·</span>
              <Bike className="h-3 w-3" /> {pref.vehicle_type || 'Vehicle'} {pref.vehicle_plate && `• ${pref.vehicle_plate}`}
              <span className="text-slate-300">·</span>
              <span className={pref.is_online ? 'text-emerald-700 font-medium' : 'text-slate-400'}>
                {pref.is_online ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => save(null)} disabled={saving} data-testid="clear-preferred-driver">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <Select value={selected || 'none'} onValueChange={(v) => save(v === 'none' ? null : v)}>
        <SelectTrigger className="h-10" data-testid="preferred-driver-select">
          <SelectValue placeholder="Pick a preferred driver" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No preference (use nearest available driver)</SelectItem>
          {data.drivers.map((d) => {
            // Disambiguate drivers with the same name using plate or last-4 of id
            const suffix = d.vehicle_plate
              ? ` • ${d.vehicle_plate}`
              : d.driver_id ? ` • #${String(d.driver_id).slice(-4)}` : '';
            const superTag = d.is_super_driver ? ' ★ Super' : '';
            return (
              <SelectItem key={d.driver_id} value={d.driver_id} data-testid={`preferred-driver-option-${d.driver_id}`}>
                {d.name}{suffix}{superTag} · {d.vehicle_type || 'Vehicle'} · {d.is_online ? 'Online' : 'Offline'}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {saving && <Loader2 className="mt-2 h-4 w-4 animate-spin text-emerald-600" />}
    </section>
  );
};

export default PreferredDriverPicker;
